import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import cn from 'classnames';
import pick from 'lodash/pick';
import { isMobile } from 'react-device-detect';

import { useToggle } from 'Shared/utils/hooks';
import WpUserInput from 'Wpds/components/user-input/WpUserInput';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import WpIcon from 'Wpds/components/icon/WpIcon';
import AssignDuplicateModal from 'Shared/components/AssignDuplicateModal/AssignDuplicateModal';
import { setViewedWorkspaceItem } from 'Shared/actions/todos';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { getWorkspaceByUuid } from 'Shared/selectors/workspace';
import DueDateDropdown from 'Shared/components/DropDowns/DueDateDropdown/DueDateDropdown';
import WorkspaceItemContainer from 'Shared/components/WorkspaceItemContainer/WorkspaceItemContainer';
import { isInThePast } from 'Shared/utils/DateTime.utils';
import { updateItem } from 'Shared/actions/todos';
import { getAssignmentAttributes } from 'Shared/helpers';
import {
  BottomDroppedPlaceholder,
  BottomParentPlaceholder,
  BottomChildrenPlaceholder,
  TopDroppedPlaceholder,
  useMobilePreviewDragLayer,
} from 'Shared/components/DragAndDropComponents';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import UserPresenceInWorkspaceItem from 'Shared/components/UserPresenceInWorkspaceItem/UserPresenceInWorkspaceItem';

import { dragSpec, dropSpec } from './actionItemDragAndDrop';
import TeammateOrGroupLink from './TeammateOrGroupLink/TeammateOrGroupLink';
import { getSourcePage } from 'Shared/utils/getSourcePage';

import '@workpatterns/draft-js-mention-plugin/lib/plugin.css';
import 'draft-js/dist/Draft.css';
import './ActionItem.scss';

const neededItemFields = Object.freeze([
  'uuid',
  'id',
  'video',
  'parentUuid',
  'completed',
  'dueBy',
  'itemType',
  'assignmentPersonId',
  'isHighPriority',
  'workspacePath',
  'workspaceId',
]);

type Props = {
  isDisabled?: boolean;
  isCompleteToggleDisabled?: boolean;
  uuid: string;
  withWorkspaceSection?: boolean;
  withOwnerSection?: boolean;
  withDueDateSection?: boolean;
  parentItemInfoComponent?: React.ReactNode;
  showPresenceComponent?: boolean;
  enableHideAnimation?: boolean;
  hideActionsAreaOnDesktop?: boolean;
  children?: React.ReactNode;
  isFocused?: boolean;
  isFirst: boolean;
  forbidDrag: boolean;
  useNestingDnd?: boolean;
  notShowAsChild?: boolean;
  hasHighPriorityItems: boolean;
  selector?: string;
  isLast?: boolean;
  onAddItem?: Function;
  nestItemByTabClick?: Function;
  onArrowUpClick?: Function;
  onDrop?: Function;
  onNestByDrag?: Function;
  onArrowDownClick?: Function;
  onFocusedTodoUpdate?: Function;
  forceShowMobileActions?: boolean;
  enableSelection?: boolean;
};

let timeoutIdForShowPreview;
let hideAnimationRemoveTimeId;

const ActionItem = ({
  isDisabled = false,
  isCompleteToggleDisabled = false,
  uuid,
  withWorkspaceSection = false,
  withOwnerSection = false,
  withDueDateSection = false,
  enableHideAnimation = false,
  parentItemInfoComponent = null,
  showPresenceComponent = false,
  hideActionsAreaOnDesktop = false,
  children,
  isFocused,
  isFirst,
  selector,
  forbidDrag,
  useNestingDnd = false,
  notShowAsChild = false,
  hasHighPriorityItems,
  onAddItem,
  nestItemByTabClick,
  onArrowUpClick,
  onArrowDownClick,
  onFocusedTodoUpdate = () => null,
  onDrop,
  onNestByDrag,
  isLast = false,
  forceShowMobileActions = false,
  enableSelection = true,
}: Props) => {
  const dispatch = useDispatch();
  const { isSelected, notInGroupSelect, isNewItem } = useDeepEqualSelector(state => {
    const { selectedUuids } = state;
    return {
      notInGroupSelect: !selectedUuids.includes(uuid) || (selectedUuids.length === 1 && selectedUuids[0] === uuid),
      isSelected: Boolean(selectedUuids.find(selectedUuid => selectedUuid === uuid)),
      isNewItem: state.workspace.workspaceNotifications?.modifiedItemsUuids?.includes(uuid),
    };
  });

  const todoData = useDeepEqualSelector(state => pick(getMeetingItemByUuid(uuid)(state), neededItemFields));
  const { video, parentUuid, dueBy, completed, itemType, isHighPriority, workspacePath, workspaceId } = todoData;

  const users = useDeepEqualSelector(state => getWorkspaceByUuid(workspaceId)(state)?.users || []);
  const assignmentAvatar = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignmentAvatar);
  const assignmentId = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignmentId);
  const assigneeUserId = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignee.userId);
  const assigneeName = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state).assignee.personName);

  const [showAssignDuplicateModal, toggleShowAssignDuplicateModal] = useToggle(false);

  const [hoverOffset, setHoverOffset] = useState(null);
  const [isDropAsChild, setIsDropAsChild] = useState(null);
  const [isHideAnimationProccess, setHideAnimationProccess] = useState(false);

  const containerRef = useRef<HTMLDivElement>();

  const { mobilePreviewDragLayer, startShowMobilePreview, cancelShowMobilePreview } = useMobilePreviewDragLayer({
    ref: containerRef,
    itemUuid: uuid,
  });

  const [, connectDragSource, dragPreview] = useDrag({
    item: { uuid, type: itemType },
    ...dragSpec({ uuid, forbidDrag, useNestingDnd, onDrop, onNestByDrag, dispatch, selector }),
  });
  const [{ isOver, canDrop, isDropOnEmptyArea = null }, connectDropTarget] = useDrop({
    accept: itemType,
    ...dropSpec({ uuid, containerRef, item: todoData, useNestingDnd, setHoverOffset, setIsDropAsChild, isLast }),
  });

  const isChildrenItemsPresented = Boolean(children);
  const hasUploadingVideo = video && !video.id;
  const isShowHideAnimation = enableHideAnimation && isHideAnimationProccess;

  useEffect(() => {
    dragPreview(getEmptyImage());
    return () => {
      clearTimeout(timeoutIdForShowPreview);
      if (hideAnimationRemoveTimeId) clearTimeout(hideAnimationRemoveTimeId);
    };
  }, [dragPreview]);

  const isDueDateInThePast = useCallback(() => !completed && dueBy && isInThePast(dueBy), [completed, dueBy]);

  const handleStartAnimationProccess = useCallback(() => {
    if (hideAnimationRemoveTimeId) clearTimeout(hideAnimationRemoveTimeId);
    setHideAnimationProccess(true);
    hideAnimationRemoveTimeId = setTimeout(() => setHideAnimationProccess(false), 600);
  }, []);

  const handleItemClick = useCallback(() => {
    if (isNewItem) dispatch(setViewedWorkspaceItem(uuid));
  }, [isNewItem, uuid, dispatch]);

  const handleAssignToMultipleClick = useCallback(() => {
    if (!showAssignDuplicateModal) {
      (window as any).analytics.track('Clicked Assign to Multiple', { sourcePage: getSourcePage() });
    }
    toggleShowAssignDuplicateModal();
  }, [toggleShowAssignDuplicateModal, showAssignDuplicateModal]);

  const handleAssignmentChange = assignedUser => {
    if (assigneeUserId === assignedUser?.id) return;

    dispatch(
      updateItem(uuid, {
        itemType,
        assignmentAttributes: getAssignmentAttributes({ assignmentId, assignedId: assignedUser?.personId }),
        assignmentPersonId: assignedUser?.personId,
        assignmentAvatar: assignedUser?.avatar,
        assignee: {
          personName: assignedUser?.fullName,
          userId: assignedUser?.id,
        },
      })
    );
  };

  const topDroppedPlaceholderActive = Boolean(!hoverOffset) && isOver && canDrop;

  const bottomDroppedPlaceholderActive =
    (Boolean(hoverOffset) && isOver && canDrop) || (Boolean(hoverOffset) && isDropOnEmptyArea && isLast);

  const AssignedComponent = () => {
    return assignmentAvatar ? (
      <div className="action-item-wrapper__owner">
        <div className="action-item-wrapper__owner__avatar">
          <WpAvatar avatar={assignmentAvatar} size="sm" />
        </div>
        <span className="action-item-wrapper__owner__name">{assigneeName}</span>
      </div>
    ) : (
      <div className="action-item-wrapper__owner">
        <div className="action-item-wrapper__unassigned-icon">
          <WpIcon name="user" />
        </div>
        <span className="action-item-wrapper__owner__name--unassigned">Unassigned</span>
      </div>
    );
  };

  return connectDropTarget(
    <div
      className={cn('action-item d-flex flex-column position-relative', {
        'action-item--is-high-priority': isHighPriority,
        'is-hide-animation-proccess': isShowHideAnimation,
        rainbow: Math.floor(Math.random() * 10) === 5, // 1 in 10 chance of special rainbow class for animation
      })}
      ref={containerRef}
      onClick={handleItemClick}
    >
      {mobilePreviewDragLayer}
      {topDroppedPlaceholderActive && <TopDroppedPlaceholder />}
      <div
        onTouchStart={startShowMobilePreview}
        onTouchMove={cancelShowMobilePreview}
        onTouchEnd={cancelShowMobilePreview}
        ref={isMobile ? connectDragSource : null}
      >
        <div
          className={cn('position-absolute', {
            'action-item-high-pri-border': isHighPriority && (!parentUuid || notShowAsChild),
          })}
        />
        <div
          className={cn('action-item-wrapper d-flex justify-content-start flex-column position-relative', {
            'action-item-wrapper--is-selected': (isSelected || isFocused) && !isShowHideAnimation,
            'action-item-wrapper--completed': completed,
            'action-item-wrapper--is-first': !parentUuid && isFirst && hasHighPriorityItems,
            'action-item-wrapper--has-childrens': isChildrenItemsPresented,
            'action-item-wrapper--is-mobile': isMobile,
            'action-item-wrapper--skip-hover-coloring': isShowHideAnimation,
          })}
        >
          <div className="action-item-editor-container d-flex position-relative">
            {showPresenceComponent && <UserPresenceInWorkspaceItem itemUuid={uuid} />}
            {!hasUploadingVideo && !forbidDrag && !isMobile ? (
              connectDragSource(
                <div>
                  <div className="action-item-drag-mark position-absolute d-flex">
                    <WpIcon name="drag" prefix="fac" size="lg" />
                  </div>
                  <div className="action-item-drag-mark--chrome-extension mx-1 d-flex h-100">
                    <WpIcon name="drag" prefix="fac" size="lg" />
                  </div>
                </div>
              )
            ) : (
              <div className="d-flex h-100 d-sm-none ml-3" />
            )}
            <div className="action-item-content-container d-flex flex-column position-relative order-1">
              <WorkspaceItemContainer
                isDisabled={isDisabled}
                isCompleteToggleDisabled={isCompleteToggleDisabled}
                uuid={uuid}
                isSelected={isSelected && enableSelection}
                isFocused={isFocused && enableSelection}
                notShowAsChild={notShowAsChild}
                notInGroupSelect={notInGroupSelect}
                hideActionsAreaOnDesktop={hideActionsAreaOnDesktop}
                onAddItem={onAddItem}
                nestItemByTabClick={nestItemByTabClick}
                onArrowUpClick={onArrowUpClick}
                onArrowDownClick={onArrowDownClick}
                onFocusedTodoUpdate={onFocusedTodoUpdate}
                onHideAnimationTrigger={handleStartAnimationProccess}
                forceShowMobileActions={forceShowMobileActions}
              />
              <div className="action-item-parent-info">{parentItemInfoComponent}</div>
            </div>
            {withOwnerSection && (
              <div
                className={cn(
                  'action-item-additional-section action-item-owner border-left px-1 d-none d-sm-block order-3'
                )}
              >
                <WpUserInput
                  users={users}
                  id="actionItemOwner"
                  label=""
                  disabled={isDisabled}
                  removeUserLabel="Unassign"
                  value={assigneeUserId}
                  components={{
                    innerDropdown: AssignedComponent,
                    dropdownFooter: () => (
                      <div className="action-item-assign-multiple-action" onClick={handleAssignToMultipleClick}>
                        <div className="action-item-assign-multiple-action__icon">
                          <WpIcon name="user" />
                        </div>
                        <span>Duplicate and assign</span>
                      </div>
                    ),
                  }}
                  onChange={handleAssignmentChange}
                  handleRemoveUser={handleAssignmentChange}
                />
              </div>
            )}
            {withDueDateSection && (
              <div
                className={cn(
                  'action-item-additional-section action-item-due-date border-left p-2 pt-2 d-none d-sm-block order-2',
                  {
                    'action-item-due-date--past-due': isDueDateInThePast(),
                  }
                )}
              >
                <DueDateDropdown disabled={isDisabled} uuid={uuid} />
              </div>
            )}
            {withWorkspaceSection && (
              <div className="action-item-additional-section  border-left p-2 pt-2 d-none d-sm-block order-4">
                <TeammateOrGroupLink workspaceId={workspaceId} itemUuid={uuid} workspacePath={workspacePath} />
              </div>
            )}
          </div>
        </div>
        {isChildrenItemsPresented && children}
      </div>

      {bottomDroppedPlaceholderActive && (
        <BottomDroppedPlaceholder>
          <BottomParentPlaceholder isActive={isOver && isDropAsChild} />
          <BottomChildrenPlaceholder />
        </BottomDroppedPlaceholder>
      )}

      {showAssignDuplicateModal && (
        <AssignDuplicateModal onToggle={handleAssignToMultipleClick} itemUuid={uuid} workspaceId={workspaceId} />
      )}
    </div>
  );
};

export const OriginalActionItem = ActionItem;

export default React.memo(ActionItem);
